// JavaScript SDK
import PocketBase, { ExternalAuthModel, RecordAuthResponse } from "pocketbase";
import { API_BASE_URL } from "./base.tsx";
import { tokenStorage } from "./tokenStorage";

const pb = new PocketBase(API_BASE_URL);

// Define types for user and authentication data if you have specific types, or use `any` if not available
interface User {
  email: string;
  password: string;
  name: string;
}

// Async function to sign up a new user
// sets the token in storage to the new signed in user
export async function signUpUser(
  email: string,
  password: string,
  name: string
): Promise<User> {
  try {
    const user = (await pb.collection("users").create({
      email: email.toLowerCase(),
      password: password,
      passwordConfirm: password,
      name: name,
    })) as User;
    await signInUser(email.toLowerCase(), password);
    return user;
  } catch (error) {
    console.error("Error signing up user:", error);
    throw error;
  }
}

// assigned
let postHogResetFunction: (() => void) | undefined = undefined;

// this is used so on logout a new session would
// possibly have the new distinct id for users
// sharing a computer see https://posthog.com/docs/product-analytics/identify
export function setPostHogResetFunction(fn: () => void) {
  postHogResetFunction = fn;
}

export function signOutUser() {
  try {
    pb.authStore.clear(); // No need to await this
    tokenStorage.clearToken();
    if (postHogResetFunction) {
      try {
        postHogResetFunction();
      } catch (err) {
        console.error(err);
      }
    }
  } catch (err) {
    console.error(err);
  }
}

// Async function to sign in a user with email and password
// sets the token in storage to the new signed in user
export async function signInUser(
  email: string,
  password: string
): Promise<RecordAuthResponse> {
  // must sign out first to clear any temp users
  signOutUser();
  try {
    const authData = await pb
      .collection("users")
      .authWithPassword(email.toLowerCase(), password);
    tokenStorage.setToken(authData.token);
    return authData;
  } catch (error) {
    console.error("Error signing in user:", error);
    throw error;
  }
}

export interface SocialSigninResponse {
  authData: RecordAuthResponse;
  email: string;
  name: string;
}

// this just provides the ui with pb.authStore.token
// further calls needed to ensure user is created
// this is also a signup function
// We dont want to overwrite the name they selected when they
// signed up as a temp user
export async function signInWithProvider(
  providerName: string,
  code: string,
  codeVerifier: string,
  redirectUrl: string,
  skipName: boolean,
  isNativeIOS: boolean
): Promise<SocialSigninResponse> {
  // must sign out first to clear any temp users
  let useVerifier = codeVerifier;
  if (isNativeIOS) {
    useVerifier = "";
  }
  // if this fails we dont want to leave the user in a
  // broken state, we will attempt to reload the auth
  // then we will send the user back to the SIGNUP page
  // if they came from there to save attempt to save the user.
  let fallBackAuthToken: string | undefined;
  try {
    fallBackAuthToken = tokenStorage.getToken();
  } catch (e) {
    console.warn("Failed to export auth cookie, user can't be saved", e);
  }

  signOutUser();

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // ms

  let res;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      res = await pb.collection("users").authWithOAuth2Code(
        providerName,
        code,
        useVerifier,
        redirectUrl,
        // pass optional user create data
        {
          emailVisibility: false,
        }
      );
      break; // If successful, exit the retry loop
    } catch (error) {
      if (attempt === MAX_RETRIES) {
        // On final attempt, let the error propagate
        if (fallBackAuthToken) {
          // tries to save the users login flow by giving them back their temp_trail user auth auth
          try {
            tokenStorage.setToken(fallBackAuthToken);
          } catch (e) {
            console.warn("Failed to load auth cookie, user can't be saved", e);
          }
        } else {
          console.warn(
            "no fallback auth, user can't be saved and will go to login screen"
          );
        }
        throw error;
      }
      console.warn(
        `OAuth authentication attempt ${attempt} failed, retrying in ${RETRY_DELAY}ms...`,
        error
      );
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      continue;
    }
  }

  if (!res) {
    throw new Error("Failed to authenticate after all retries");
  }

  const email = res.meta?.email || "";
  // Hence we should really prevent sign up flow if they have an account already
  // And if they create an account on the login page thats not an issue
  const pbUid = res.record?.id || "";
  const name =
    res.meta?.rawUser?.given_name ||
    res.meta?.name ||
    email.split("@")[0] || // Worse case scenario
    "";
  const avatarUrl = res.meta?.avatarUrl || "";

  // Add retries for the update operation as well
  if (pbUid && !skipName) {
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        await pb.collection("users").update(pbUid, {
          name: name,
          avatarUrl: avatarUrl,
        });
        break;
      } catch (error) {
        if (attempt === MAX_RETRIES) {
          console.warn(
            "Failed to update user profile after all retries",
            error
          );
          break; // Don't throw, just log warning as requested
        }
        console.warn(
          `Profile update attempt ${attempt} failed, retrying in ${RETRY_DELAY}ms...`,
          error
        );
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      }
    }
  }

  const token = pb.authStore.token;
  tokenStorage.setToken(token);

  return {
    authData: res,
    email: email.toLowerCase(),
    name: name,
  };
}

export async function updateName(name: string): Promise<void> {
  const pbUid = pb.authStore.model?.id;
  if (!pbUid) {
    throw new Error("No user id found");
  }
  await pb.collection("users").update(pbUid, {
    name: name,
  });
}

// Async function to send a verification email
export async function sendVerificationEmail(email: string): Promise<void> {
  try {
    await pb.collection("users").requestVerification(email.toLowerCase());
    console.log("Verification email sent");
  } catch (error) {
    console.error("Error sending verification email:", error);
    throw error;
  }
}

// Async function to send a password reset email
export async function sendPasswordResetEmail(email: string): Promise<void> {
  try {
    await pb.collection("users").requestPasswordReset(email.toLowerCase());
    console.log("Password reset email sent");
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
}

export async function confirmPasswordReset(
  token: string,
  password: string,
  passwordConfirm: string
): Promise<void> {
  try {
    await pb
      .collection("users")
      .confirmPasswordReset(token, password, passwordConfirm);
    console.log("Password reset successfully");
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
}

export async function deletePocketBaseUser(): Promise<void> {
  const authRecord = pb.authStore.model;
  if (!authRecord) {
    throw new Error("No auth record found");
  }
  await pb.collection("users").delete(authRecord.id);
}

// tries its best to unlink all auths. if there are not anything we dont care about errors
export async function unlinkExternalAuths(): Promise<void> {
  const authRecord = pb.authStore.model;
  if (!authRecord) {
    throw new Error("No auth record found");
  }
  let auths: ExternalAuthModel[] = [];
  try {
    auths = await pb.collection("users").listExternalAuths(authRecord.id);
    for (const auth of auths) {
      try {
        await pb
          .collection("users")
          .unlinkExternalAuth(authRecord.id, auth.provider);
      } catch (error) {
        console.error("Error unlinking external auth:", error);
      }
    }
  } catch (error) {
    console.error("Error listing external auths:", error);
  }
  console.log("Unlinking external auths1:", auths);
  if (auths.length === 0) {
    return;
  }
  console.log("Unlinking external auths2:", auths);
  for (const auth of auths) {
    try {
      await pb
        .collection("users")
        .unlinkExternalAuth(authRecord.id, auth.provider);
    } catch (error) {
      console.error("Error unlinking external auth:", error);
    }
  }
}
